import type { RankingSource } from '../core/types.ts';
import type { AccountSnapshot, SleeperProfile } from './types.ts';

export const MAX_RANKING_SOURCES = 25;
export const MAX_PROFILE_BYTES = 128 * 1024;
export const MAX_SOURCE_BYTES = 1024 * 1024;

export class SyncValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SyncValidationError';
  }
}

export class SyncConflictError extends Error {
  constructor(message = 'This profile changed on another device.') {
    super(message);
    this.name = 'SyncConflictError';
  }
}

export function validateProfile(profile: SleeperProfile): void {
  if (!/^\S.{0,63}$/.test(profile.sleeper_username.trim())) {
    throw new SyncValidationError('Sleeper username is invalid.');
  }
  if (!/^\S.{0,63}$/.test(profile.sleeper_user_id.trim())) {
    throw new SyncValidationError('Sleeper user ID is invalid.');
  }
  if (byteLength(profile.setup) > MAX_PROFILE_BYTES) {
    throw new SyncValidationError('Saved setup is too large.');
  }
}

export function validateSources(sources: RankingSource[]): void {
  if (sources.length > MAX_RANKING_SOURCES) {
    throw new SyncValidationError(`You can save at most ${MAX_RANKING_SOURCES} ranking sources.`);
  }
  for (const source of sources) {
    if (!/^\S.{0,127}$/.test(source.source_id)) throw new SyncValidationError('Ranking source ID is invalid.');
    if (byteLength(source) > MAX_SOURCE_BYTES) {
      throw new SyncValidationError(`Ranking source "${source.source_name}" is too large.`);
    }
  }
}

export function validateSnapshot(snapshot: AccountSnapshot): void {
  if (new Set(snapshot.profiles.map((profile) => profile.sleeper_user_id)).size !== snapshot.profiles.length) {
    throw new SyncValidationError('Saved Sleeper profiles must be unique.');
  }
  for (const profile of snapshot.profiles) validateProfile(profile);
  validateSources(snapshot.sources);
}

function byteLength(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}
